import { baseClasses } from '@weatherbug/common';
import { Story, PrevNextDetails, PopupStoryResult } from '../../components/common/utilities/newsStoryPopupTypes';
import { getPageSpecificPopupData } from '../../components/common/utilities/newsStoryPopupUtils';
import { NewsStoryPoupProvider } from '../providers/storyPopupProvider';

const { AsyncAction } = baseClasses;
export const GET_POPUP_STORY = new AsyncAction('GET_POPUP_STORY');

export const SET_POPUP_VIEW_STORIES = 'SET_POPUP_VIEW_STORIES';
export const SET_STORIES_POPUP_RETURN_PATH_INFO_AND_PARAMS = 'SET_STORIES_POPUP_RETURN_PATH_INFO_AND_PARAMS';
export const CLEAR_STORIES_POPUP_DATA = 'CLEAR_STORIES_POPUP_DATA';
export const SET_SOURCE_STORIES = 'SET_SOURCE_STORIES';

interface PopupViewData {
    currentStoryIndex?: number;
    nextStoryIndex?: number;
    prevStoryIndex?: number;
    isCurrentStoryWithMapsOrIndexTag?: boolean;
    popupStories?: Story[];
    currentStory?: Story;
    nextStoryDetails?: PrevNextDetails;
    prevStoryDetails?: PrevNextDetails;
}

interface StoryPopupReturnPathInfo {
    sourceRoute: string;
    sourceRouteName: string;
}

export type SetPopupViewStoriesActionType = {
    type: typeof SET_POPUP_VIEW_STORIES;
    payload: PopupViewData;
}

export type SetSourceStoriesActionType = {
    type: typeof SET_SOURCE_STORIES;
    payload: Story[];
}

export type SetPopupReturnPathInfoAndParamsActionType = {
    type: typeof SET_STORIES_POPUP_RETURN_PATH_INFO_AND_PARAMS;
    payload: StoryPopupReturnPathInfo;
}

export type GetPopupStoryActionType = {
    type: typeof GET_POPUP_STORY;
    payload: Promise<Story | any>;
}

export type ClearPopupDataType = {
    type: typeof CLEAR_STORIES_POPUP_DATA;
    payload?: any;
}

export function clearPopupDataAction(): ClearPopupDataType {
    return {
        type: CLEAR_STORIES_POPUP_DATA
    };
}

export function setAllStories(stories: Story[]): SetSourceStoriesActionType {
    return {
        type: SET_SOURCE_STORIES,
        payload: stories
    };
}

export function handleStoryNavigation(newCurrentStoryIndex?: number ): any {
    return (dispatch, getState) => {
        const currState = getState();
        const popupStories: Story[] = currState?.newsStoryPopup?.popupStories;
        if (typeof newCurrentStoryIndex === 'number' && newCurrentStoryIndex >= 0 && popupStories?.length) {
            const currentStoryRoute: string = popupStories[newCurrentStoryIndex]?.NodeAlias;
            dispatch(setPopupViewStoriesAction(currentStoryRoute, popupStories, newCurrentStoryIndex));
        }
    };
}

export function setPopupReturnPathInfoAndParamsAction(sourcePageRoute: string): SetPopupReturnPathInfoAndParamsActionType {
    const { sourceRoute, sourceRouteName } = getPageSpecificPopupData(sourcePageRoute);
    return {
        type: SET_STORIES_POPUP_RETURN_PATH_INFO_AND_PARAMS,
        payload: {
            sourceRoute,
            sourceRouteName
        }
    };
}

export function setPopupViewStoriesAction(currentStoryRoute: string, stories: Story[] = [], currentStoryIndex?: number): SetPopupViewStoriesActionType {
    const payload: PopupViewData = {
        currentStoryIndex: undefined,
        nextStoryIndex: undefined,
        prevStoryIndex: undefined,
        isCurrentStoryWithMapsOrIndexTag: false,
        popupStories: stories,
        currentStory: undefined,
        nextStoryDetails: undefined,
        prevStoryDetails: undefined
    };
    
    if (typeof currentStoryIndex === 'number' && currentStoryIndex >= 0) {
        payload.currentStoryIndex = currentStoryIndex;
    } else if (currentStoryRoute) {
        payload.currentStoryIndex = stories?.findIndex(story => story?.NodeAlias?.toLocaleLowerCase() === currentStoryRoute?.toLocaleLowerCase());
    }

    if (typeof payload.currentStoryIndex === 'number' && payload.currentStoryIndex >= 0) {
        const currentStory = stories?.[payload.currentStoryIndex];
        payload.isCurrentStoryWithMapsOrIndexTag = currentStory?.ContentTags?.includes('map') 
            || currentStory?.ContentTags?.includes('maps') 
            || currentStory?.ContentTags?.includes('index');
        
        const nextStoryIndex = payload.currentStoryIndex < stories?.length - 1 ? payload.currentStoryIndex + 1 : undefined;
        const prevStoryIndex = payload.currentStoryIndex > 0 && stories?.length ? payload.currentStoryIndex - 1 : undefined;
        const nextStory: Story | undefined = stories?.[nextStoryIndex];
        const prevStory: Story | undefined = stories?.[prevStoryIndex];
        const nextStoryDetails: PrevNextDetails = {
            storyId: nextStory?.Id,
            storyImageUrl: nextStory?.ThumbnailImageLink,
            storyTitle: nextStory?.Title,
            storyUrl: nextStory?.NodeAlias,
            storyVideoUrl: nextStory?.VideoLinks
        };
        const prevStoryDetails: PrevNextDetails = {
            storyId: prevStory?.Id,
            storyImageUrl: prevStory?.ThumbnailImageLink,
            storyTitle: prevStory?.Title,
            storyUrl: prevStory?.NodeAlias,
            storyVideoUrl: prevStory?.VideoLinks
        };

        payload.currentStory = currentStory;
        payload.nextStoryIndex = nextStoryIndex;
        payload.prevStoryIndex = prevStoryIndex;
        payload.nextStoryDetails = nextStoryDetails;
        payload.prevStoryDetails = prevStoryDetails;
    }

    return {
        type: SET_POPUP_VIEW_STORIES,
        payload
    };
}

export function getPopupStory(storyId: string, type?: string): GetPopupStoryActionType {
    return {
        type: GET_POPUP_STORY.base,
        payload: getPopupStoryFallback(storyId, type)
    };
}

async function getPopupStoryFallback(storyId: string, type?: string): Promise<Story | any> {
    const op = new NewsStoryPoupProvider();
    return await op.getPopupNewsStory(storyId, type);
}